/*
 * certificate-maker/editor.js — direct manipulation on the sheet.
 *
 * WHY THIS FILE EXISTS
 * ────────────────────
 * The first version of this tool was a six-step wizard with a preview beside
 * it. Everything worked and nothing felt like a design tool, because the object
 * on screen was not the object you could touch: to change the name you hunted
 * for a field in step 2, but to MOVE it you dragged it on the canvas. Two
 * mental models for one thing, and neither is the one people arrive with.
 *
 * What people arrive with is Canva's: click the thing, edit the thing.
 *
 * THREE RULES THIS FILE KEEPS
 * ───────────────────────────
 *  1. The canvas never owns state. A drag writes an OFFSET into the document
 *     and asks for a redraw; the flow layout keeps running underneath, so a
 *     nudged block still moves with the composition when the page size or the
 *     frame changes. Absolute coordinates are why drag-and-drop editors fall
 *     apart the moment you switch A4 landscape to portrait.
 *  2. Every change that a person would think of as "one action" is ONE history
 *     entry. A drag is one entry, not sixty; typing a word is one entry, not
 *     seven. Undo that gives back a third of a gesture is worse than none.
 *  3. Nothing snaps invisibly. When a drag snaps, the guide that caused it is
 *     drawn — otherwise the sheet moves by itself and the person is left
 *     wondering what they did.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CertEditor = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var NS = 'http://www.w3.org/2000/svg';

    /* Snapping distance in MILLIMETRES of the sheet, not pixels of the screen.
     * In pixels the same gesture would snap harder on a small preview than on a
     * big one, which is the sort of thing that feels broken without being
     * describable. 1.2 mm is about 4 px at a typical zoom — close enough to
     * catch an intent, far enough not to fight one. */
    var SNAP_MM = 1.2;

    function el(name, attrs) {
        var n = document.createElementNS(NS, name);
        for (var k in attrs) {
            if (Object.prototype.hasOwnProperty.call(attrs, k) && attrs[k] != null) n.setAttribute(k, String(attrs[k]));
        }
        return n;
    }

    /**
     * create(opts) → editor
     *
     *   opts.svg        the <svg> the sheet is drawn into
     *   opts.host       positioned element the inline text box can live in
     *   opts.getState   () → the document state object (mutated in place)
     *   opts.getResult  () → the last CertLayout.build() result
     *   opts.onChange   (reason) → caller redraws; reason is a telemetry label
     *   opts.onSelect   (id|null, box) → caller updates the inspector/toolbar
     *   opts.onHistory  ({canUndo, canRedo}) → caller updates the buttons
     */
    function create(opts) {
        var svg = opts.svg;
        var host = opts.host || svg.parentNode;
        var sel = null;                  // selected block/overlay id
        var drag = null;
        var editing = null;
        /* Reported once per kind: the question is whether direct manipulation is
           DISCOVERED, and a drag fires per pointermove. */
        var told = {};
        function tell(what, data) {
            if (told[what] || !window.__aeFunnel) return;
            told[what] = 1;
            window.__aeFunnel(what, data || {});
        }

        var overlay = null;              // <g> holding selection + guides
        var liveGuides = null;           // guides of the gesture in progress
        var hist = { stack: [], ptr: -1, MAX: 60 };
        var suppress = false;

        /* ── geometry ──────────────────────────────────────────────────────── */

        function scale() {
            var r = svg.getBoundingClientRect();
            var page = opts.getResult().page;
            return { k: r.width / page.w, rect: r, page: page };
        }
        /* Client point → millimetres on the sheet. */
        function toMm(ev) {
            var s = scale();
            return { x: (ev.clientX - s.rect.left) / s.k, y: (ev.clientY - s.rect.top) / s.k, s: s };
        }

        /** The placed box of a block, in millimetres, with its anchor resolved. */
        function boxOf(id) {
            var res = opts.getResult();
            var b = (res.blocks || []).filter(function (q) { return q.id === id; })[0];
            if (!b) return null;
            var x = b.overlay ? b.x
                : b.anchor === 'middle' ? b.x - b.w / 2
                : b.anchor === 'end' ? b.x - b.w : b.x;
            return { id: id, x: x, y: b.y, w: Math.max(4, b.w), h: Math.max(3, b.h), overlay: !!b.overlay, raw: b };
        }

        /* ── history ───────────────────────────────────────────────────────── */

        function snapshot() {
            var st = opts.getState();
            return JSON.stringify({ texts: st.texts, nudge: st.nudge, overlays: st.overlays, style: st.style });
        }

        function commit(reason) {
            if (suppress) return;
            var snap = snapshot();
            if (hist.ptr >= 0 && hist.stack[hist.ptr] === snap) return;
            hist.stack.length = hist.ptr + 1;
            hist.stack.push(snap);
            if (hist.stack.length > hist.MAX) hist.stack.shift();
            hist.ptr = hist.stack.length - 1;
            reportHistory();
        }

        function step(dir) {
            var target = hist.ptr + dir;
            if (target < 0 || target >= hist.stack.length) return false;
            hist.ptr = target;
            var snap = JSON.parse(hist.stack[target]);
            var st = opts.getState();
            st.texts = snap.texts;
            st.nudge = snap.nudge;
            st.overlays = snap.overlays;
            st.style = snap.style;
            /* The selected element may not exist in the restored state. */
            if (sel && !hasId(sel)) sel = null;
            reportHistory();
            opts.onChange(dir < 0 ? 'undo' : 'redo');
            return true;
        }

        function hasId(id) {
            var st = opts.getState();
            if (st.nudge && st.nudge[id]) return true;
            return (st.overlays || []).some(function (o) { return o.id === id; })
                || !!(opts.getResult().blocks || []).filter(function (b) { return b.id === id; })[0];
        }

        function reportHistory() {
            if (opts.onHistory) opts.onHistory({ canUndo: hist.ptr > 0, canRedo: hist.ptr < hist.stack.length - 1 });
        }

        /* ── selection ─────────────────────────────────────────────────────── */

        function select(id, why) {
            if (editing) stopEdit(true);
            sel = id;
            paint();
            if (opts.onSelect) opts.onSelect(id, id ? boxOf(id) : null, why || 'click');
        }

        /** Redraws the selection frame and any live guides. Cheap: one <g>.
         *
         * ⚠️ The guides are REMEMBERED for the duration of a gesture. A drag
         * paints them and then asks the caller to redraw; the caller's redraw
         * calls repaint() with no arguments, which used to wipe them within the
         * same frame — so the snap worked and its explanation never appeared
         * (measured 2026-08-23: the block jumped to the centre line and nothing
         * said why). A snap nobody can see is the sheet moving by itself. */
        function paint(guides) {
            if (guides !== undefined) liveGuides = guides;
            guides = drag ? (liveGuides || []) : null;
            if (!drag) liveGuides = null;
            if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
            overlay = null;
            var res = opts.getResult();
            if (!res) return;
            overlay = el('g', { 'data-editor-overlay': '1', 'pointer-events': 'none' });

            (guides || []).forEach(function (g) {
                overlay.appendChild(el('line', {
                    x1: g.x1, y1: g.y1, x2: g.x2, y2: g.y2,
                    stroke: '#f43f5e', 'stroke-width': 0.25, 'stroke-dasharray': '2 1.6'
                }));
            });

            var b = sel && boxOf(sel);
            if (b) {
                var pad = 1.2;
                overlay.appendChild(el('rect', {
                    x: b.x - pad, y: b.y - pad, width: b.w + pad * 2, height: b.h + pad * 2,
                    fill: 'none', stroke: '#3b82f6', 'stroke-width': 0.35, rx: 0.8
                }));
                /* Corner ticks rather than resize handles: the size of a text
                 * block is decided by the type size, not by dragging a corner,
                 * and offering a handle that does not resize is a lie. */
                var t = 2.2;
                [[b.x, b.y, 1, 1], [b.x + b.w, b.y, -1, 1],
                 [b.x, b.y + b.h, 1, -1], [b.x + b.w, b.y + b.h, -1, -1]].forEach(function (c) {
                    overlay.appendChild(el('path', {
                        d: 'M' + (c[0] - pad) + ' ' + (c[1] - pad + c[3] * t) + 'V' + (c[1] - pad) +
                           'H' + (c[0] - pad + c[2] * t),
                        transform: 'translate(' + (c[2] < 0 ? 2 * pad : 0) + ' ' + (c[3] < 0 ? 2 * pad : 0) + ')',
                        fill: 'none', stroke: '#3b82f6', 'stroke-width': 0.55, 'stroke-linecap': 'round'
                    }));
                });
            }
            svg.appendChild(overlay);
        }

        /* ── snapping ──────────────────────────────────────────────────────── */

        /* Candidate lines the dragged box can align to: the page centre and its
         * own margins, the content column, and every other block's centre and
         * edges. Computed per drag, not per move — the sheet does not change
         * under the pointer. */
        function snapTargets(exceptId) {
            var res = opts.getResult();
            var page = res.page, c = res.content;
            var v = [page.w / 2], h = [page.h / 2];
            if (c) {
                v.push(c.x, c.x + c.w);
                h.push(c.y, c.y + c.h);
            }
            (res.blocks || []).forEach(function (b) {
                if (b.id === exceptId) return;
                var bx = b.overlay ? b.x : b.anchor === 'middle' ? b.x - b.w / 2 : b.anchor === 'end' ? b.x - b.w : b.x;
                v.push(bx, bx + b.w / 2, bx + b.w);
                h.push(b.y, b.y + b.h / 2, b.y + b.h);
            });
            return { v: v, h: h };
        }

        function applySnap(box, targets) {
            var out = { dx: 0, dy: 0, guides: [] };
            var res = opts.getResult(), page = res.page;
            var edgesX = [{ at: box.x, off: 0 }, { at: box.x + box.w / 2, off: box.w / 2 }, { at: box.x + box.w, off: box.w }];
            var edgesY = [{ at: box.y, off: 0 }, { at: box.y + box.h / 2, off: box.h / 2 }, { at: box.y + box.h, off: box.h }];
            var bestX = null, bestY = null;
            edgesX.forEach(function (e) {
                targets.v.forEach(function (t) {
                    var d = t - e.at;
                    if (Math.abs(d) <= SNAP_MM && (!bestX || Math.abs(d) < Math.abs(bestX.d))) bestX = { d: d, at: t };
                });
            });
            edgesY.forEach(function (e) {
                targets.h.forEach(function (t) {
                    var d = t - e.at;
                    if (Math.abs(d) <= SNAP_MM && (!bestY || Math.abs(d) < Math.abs(bestY.d))) bestY = { d: d, at: t };
                });
            });
            if (bestX) { out.dx = bestX.d; out.guides.push({ x1: bestX.at, y1: 0, x2: bestX.at, y2: page.h }); }
            if (bestY) { out.dy = bestY.d; out.guides.push({ x1: 0, y1: bestY.at, x2: page.w, y2: bestY.at }); }
            return out;
        }

        /* ── dragging ──────────────────────────────────────────────────────── */

        function beginDrag(ev, id) {
            var box = boxOf(id);
            if (!box) return;
            var st = opts.getState();
            var p = toMm(ev);
            drag = {
                id: id, box: box, startX: p.x, startY: p.y, moved: false,
                targets: snapTargets(id),
                base: box.overlay
                    ? (function () {
                        var ov = (st.overlays || []).filter(function (o) { return o.id === id; })[0];
                        return ov ? { x: ov.x, y: ov.y, ov: ov } : null;
                    })()
                    : { dx: (st.nudge[id] || {}).dx || 0, dy: (st.nudge[id] || {}).dy || 0 }
            };
            if (!drag.base) { drag = null; return; }
            drag.pointerId = ev.pointerId;
        }

        function moveDrag(ev) {
            if (!drag) return;
            var p = toMm(ev);
            var dx = p.x - drag.startX, dy = p.y - drag.startY;
            if (!drag.moved && Math.abs(dx) < 0.4 && Math.abs(dy) < 0.4) return;   // a click, not a drag
            if (!drag.moved) {
                drag.moved = true;
                tell('canvas_drag', { block: drag.id });
                /* The floating toolbar sits over the sheet; leaving it up during
                   a drag hides the very thing being positioned. */
                if (opts.onDragStart) opts.onDragStart(drag.id);
                /* ⚠️ Capture starts HERE, not on pointerdown. A captured pointer
                 * retargets the compatibility mouse events to the capturing
                 * element, so `dblclick` arrived on the <svg> itself with no
                 * data-block on it — and edit-in-place, the whole point of the
                 * canvas, silently did nothing (found 2026-08-23 by driving the
                 * page). Capturing only once a real drag has begun keeps the
                 * pointer inside the sheet without eating the double click. */
                try { svg.setPointerCapture(ev.pointerId); } catch (e) {}
            }

            var want = { x: drag.box.x + dx, y: drag.box.y + dy, w: drag.box.w, h: drag.box.h };
            var snap = ev.altKey ? { dx: 0, dy: 0, guides: [] } : applySnap(want, drag.targets);
            dx += snap.dx; dy += snap.dy;

            var st = opts.getState();
            if (drag.box.overlay) {
                var page = opts.getResult().page;
                drag.base.ov.x = clamp(drag.base.x + dx / page.w, -0.15, 1.15);
                drag.base.ov.y = clamp(drag.base.y + dy / page.h, -0.15, 1.15);
            } else {
                st.nudge[drag.id] = { dx: r1(drag.base.dx + dx), dy: r1(drag.base.dy + dy) };
            }
            opts.onChange('drag');
            paint(snap.guides);
        }

        function endDrag(ev) {
            if (!drag) return;
            var moved = drag.moved;
            var id = drag.id;
            drag = null;
            liveGuides = null;
            try { svg.releasePointerCapture(ev.pointerId); } catch (e) {}
            if (moved && opts.onDragEnd) opts.onDragEnd(id);
            paint();
            if (moved) commit('drag');
            if (opts.onSelect) opts.onSelect(id, boxOf(id), 'drag');
        }

        /* ── inline editing ────────────────────────────────────────────────────
         * A text box positioned over the glyphs, in the same face and size, so
         * the sheet does not visibly change when editing starts. The value goes
         * back into the SAME field the side panel edits — there is one text, not
         * an editor copy of it. */
        function startEdit(id) {
            tell('canvas_edit', { block: id });
            var res = opts.getResult();
            var prim = res.prims.filter(function (p) { return p.type === 'text' && p.blockId === id; })[0];
            var box = boxOf(id);
            if (!box || !prim || !opts.fieldOf) return;
            /* The caller may need to reshape the state before the box opens —
               the foot line is built from two fields and collapses into one when
               it is edited by hand. */
            if (opts.beforeEdit) opts.beforeEdit(id);
            var field = opts.fieldOf(id);
            if (!field) return;

            var s = scale();
            var st = opts.getState();
            var ta = document.createElement('textarea');
            ta.className = 'cm-inline-edit';
            ta.value = st.texts[field] == null ? '' : st.texts[field];
            ta.spellcheck = false;
            ta.setAttribute('aria-label', opts.labelOf ? opts.labelOf(id) : field);
            var pad = 2;
            ta.style.cssText = [
                'position:absolute',
                'left:' + ((box.x - pad) * s.k) + 'px',
                'top:' + ((box.y - pad) * s.k) + 'px',
                'width:' + ((box.w + pad * 2) * s.k) + 'px',
                'height:' + ((box.h + pad * 2) * s.k) + 'px',
                'font-size:' + (prim.size * s.k) + 'px',
                'font-family:' + (opts.cssFor ? opts.cssFor(prim.font) : 'inherit'),
                'font-weight:' + (prim.bold ? 700 : 400),
                'line-height:1.1',
                'text-align:' + (prim.anchor === 'start' ? 'left' : prim.anchor === 'end' ? 'right' : 'center'),
                'color:' + prim.color
            ].join(';');
            host.appendChild(ta);
            editing = { id: id, field: field, ta: ta, before: ta.value };
            ta.focus();
            ta.select();

            ta.addEventListener('input', function () {
                st.texts[field] = ta.value;
                opts.onChange('type');
            });
            ta.addEventListener('keydown', function (e) {
                if (e.key === 'Escape') { e.preventDefault(); stopEdit(false); }
                /* Enter commits; Shift+Enter is a real line break, because a
                 * reason legitimately has two lines. */
                else if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); stopEdit(true); }
                e.stopPropagation();
            });
            ta.addEventListener('blur', function () { stopEdit(true); });
        }

        function stopEdit(keep) {
            if (!editing) return;
            var e = editing;
            editing = null;
            if (!keep) {
                opts.getState().texts[e.field] = e.before;
                opts.onChange('edit_cancel');
            }
            if (e.ta.parentNode) e.ta.parentNode.removeChild(e.ta);
            /* One history entry for the whole typing session, not one per key. */
            if (keep && opts.getState().texts[e.field] !== e.before) commit('type');
            svg.focus({ preventScroll: true });
        }

        /* ── wiring ────────────────────────────────────────────────────────── */

        function hitId(target) {
            var n = target && target.closest ? target.closest('[data-block]') : null;
            return n ? n.getAttribute('data-block') : null;
        }

        /* ⚠️ NO preventDefault() HERE. Chrome synthesises the compatibility mouse
         * events — including dblclick — from the pointer sequence, and a
         * pointerdown whose default was prevented never produces them. The first
         * version called it to stop the browser text-selecting the sheet, and
         * the cost was that double-clicking a line did nothing at all: the whole
         * edit-in-place feature was dead and nothing reported it (found
         * 2026-08-23 by driving the page). Text selection is suppressed with CSS
         * instead, and the default is only cancelled once a drag is real. */
        svg.addEventListener('pointerdown', function (ev) {
            if (editing) { stopEdit(true); }
            var id = hitId(ev.target);
            if (!id) { select(null, 'empty'); return; }
            if (id !== sel) select(id, 'click');
            beginDrag(ev, id);
        });
        svg.addEventListener('pointermove', function (ev) {
            if (drag) ev.preventDefault();
            moveDrag(ev);
        });
        svg.addEventListener('pointerup', endDrag);
        svg.addEventListener('pointercancel', endDrag);
        svg.addEventListener('dblclick', function (ev) {
            /* Falls back to the current selection: the first click of the pair
             * has already selected the block, and a retargeted event (capture,
             * or a click that lands a pixel off the hit box) must not turn a
             * deliberate double-click into nothing. */
            var id = hitId(ev.target) || sel;
            if (id) { select(id, 'dblclick'); startEdit(id); ev.preventDefault(); }
        });

        /* The sheet is focusable so the keyboard works without a pointer — and
         * so a screen reader announces it as an interactive region. */
        if (!svg.hasAttribute('tabindex')) svg.setAttribute('tabindex', '0');

        svg.addEventListener('keydown', function (ev) {
            var mod = ev.ctrlKey || ev.metaKey;
            var kl = window.__keyLetter ? window.__keyLetter(ev) : String(ev.key || '').toLowerCase();
            if (mod && kl === 'z') {
                ev.preventDefault();
                step(ev.shiftKey ? 1 : -1);
                return;
            }
            if (mod && kl === 'y') { ev.preventDefault(); step(1); return; }
            if (ev.key === 'Escape') { select(null, 'esc'); return; }
            if (ev.key === 'Enter' && sel) { ev.preventDefault(); startEdit(sel); return; }
            if (ev.key === 'Tab' && (opts.tabOrder || []).length) {
                ev.preventDefault();
                var order = opts.tabOrder();
                var i = order.indexOf(sel);
                select(order[(i + (ev.shiftKey ? -1 : 1) + order.length) % order.length], 'tab');
                return;
            }
            if (!sel) return;
            var step_mm = ev.shiftKey ? 5 : 0.5;
            var d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[ev.key];
            if (!d) return;
            ev.preventDefault();
            nudge(sel, d[0] * step_mm, d[1] * step_mm);
            /* Held arrows should not fill the undo stack with 40 entries; the
             * commit lands when the key is released. */
            clearTimeout(nudge._t);
            nudge._t = setTimeout(function () { commit('nudge'); }, 350);
        });

        function nudge(id, dx, dy) {
            tell('canvas_nudge', { block: id });
            var st = opts.getState();
            var box = boxOf(id);
            if (!box) return;
            if (box.overlay) {
                var ov = (st.overlays || []).filter(function (o) { return o.id === id; })[0];
                if (!ov) return;
                var page = opts.getResult().page;
                ov.x = clamp(ov.x + dx / page.w, -0.15, 1.15);
                ov.y = clamp(ov.y + dy / page.h, -0.15, 1.15);
            } else {
                var n = st.nudge[id] || { dx: 0, dy: 0 };
                st.nudge[id] = { dx: r1(n.dx + dx), dy: r1(n.dy + dy) };
            }
            opts.onChange('nudge');
            paint();
        }

        function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
        function r1(v) { return Math.round(v * 10) / 10; }

        return {
            select: select,
            selected: function () { return sel; },
            repaint: paint,
            commit: commit,
            undo: function () { return step(-1); },
            redo: function () { return step(1); },
            canUndo: function () { return hist.ptr > 0; },
            canRedo: function () { return hist.ptr < hist.stack.length - 1; },
            reset: function () { hist.stack = []; hist.ptr = -1; commit('init'); },
            /* Used when the caller changes state itself (a panel control): the
             * editor must not record an entry for every slider tick, so the
             * caller says when a gesture ENDED. */
            silence: function (fn) { suppress = true; try { fn(); } finally { suppress = false; } },
            edit: startEdit,
            box: boxOf,
            SNAP_MM: SNAP_MM
        };
    }

    return { create: create };
});
